import { ApiResult, HttpClient } from "../http.js";
import {
  ByggeBasenSubgroupDto,
  ImportPriceListResultDto,
  SaveByggeBasenMappingsResultDto,
  PagedSupplierPriceItemsDto,
  PriceListImportDto,
  PriceListSupplierDto,
  SupplierGroupDto,
  SupplierPriceListFormat,
} from "../types/price-lists.js";

export interface CreatePriceListSupplierPayload {
  name: string;
  code: string;
  format: SupplierPriceListFormat;
  /** Omit to default to true — most trælaster follow ByggeBasen. */
  usesByggeBasenGroups?: boolean;
}

export interface UpdatePriceListSupplierPayload {
  name: string;
  format: SupplierPriceListFormat;
  isActive: boolean;
  /** Omit to leave unchanged. */
  usesByggeBasenGroups?: boolean;
}

export interface SearchSupplierPriceItemsQuery {
  search?: string;
  supplierId?: string;
  materialTypeId?: string;
  tradeGroupId?: string;
  groupCode?: string;
  uncategorizedOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface SupplierGroupMappingInput {
  groupCode: string;
  materialTypeId: string;
  displayName?: string | null;
}

export interface CompanyByggeBasenMappingInput {
  subgroupCode: string;
  materialTypeId: string;
}

/**
 * Company-scoped supplier price books (Bygma, AO, …). The caller builds the
 * `FormData` for `import` on their platform; this package only forwards it as
 * the request body and does not construct one itself.
 */
export class PriceListsApi {
  constructor(private readonly http: HttpClient) {}

  listSuppliers(): Promise<ApiResult<PriceListSupplierDto[]>> {
    return this.http.get<PriceListSupplierDto[]>("/api/price-lists/suppliers");
  }

  createSupplier(payload: CreatePriceListSupplierPayload): Promise<ApiResult<PriceListSupplierDto>> {
    return this.http.post<PriceListSupplierDto>("/api/price-lists/suppliers", { body: payload });
  }

  updateSupplier(
    id: string,
    payload: UpdatePriceListSupplierPayload,
  ): Promise<ApiResult<PriceListSupplierDto>> {
    return this.http.put<PriceListSupplierDto>(
      `/api/price-lists/suppliers/${encodeURIComponent(id)}`,
      { body: payload },
    );
  }

  /** `form` must contain a single `file` field holding the price file. */
  import(supplierId: string, form: FormData): Promise<ApiResult<ImportPriceListResultDto>> {
    return this.http.post<ImportPriceListResultDto>(
      `/api/price-lists/suppliers/${encodeURIComponent(supplierId)}/imports`,
      { body: form },
    );
  }

  listImports(supplierId: string, limit = 20): Promise<ApiResult<PriceListImportDto[]>> {
    return this.http.get<PriceListImportDto[]>(
      `/api/price-lists/suppliers/${encodeURIComponent(supplierId)}/imports?limit=${limit}`,
    );
  }

  searchItems(query: SearchSupplierPriceItemsQuery): Promise<ApiResult<PagedSupplierPriceItemsDto>> {
    // Built by hand rather than with URLSearchParams: this package is also typechecked
    // by clients/rn-hooks, whose tsconfig has no DOM lib, so the DOM globals are not
    // available here. Same reason bilag.ts never constructs a FormData.
    const params: string[] = [];
    const add = (key: string, value: string) =>
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);

    if (query.search) add("search", query.search);
    if (query.supplierId) add("supplierId", query.supplierId);
    if (query.materialTypeId) add("materialTypeId", query.materialTypeId);
    if (query.tradeGroupId) add("tradeGroupId", query.tradeGroupId);
    if (query.groupCode) add("groupCode", query.groupCode);
    if (query.uncategorizedOnly) add("uncategorizedOnly", "true");
    add("page", String(query.page ?? 1));
    add("pageSize", String(query.pageSize ?? 50));

    return this.http.get<PagedSupplierPriceItemsDto>(`/api/price-lists/items?${params.join("&")}`);
  }

  listGroups(supplierId: string): Promise<ApiResult<SupplierGroupDto[]>> {
    return this.http.get<SupplierGroupDto[]>(
      `/api/price-lists/suppliers/${encodeURIComponent(supplierId)}/groups`,
    );
  }

  saveGroupMappings(
    supplierId: string,
    mappings: SupplierGroupMappingInput[],
  ): Promise<ApiResult<number>> {
    return this.http.put<number>(
      `/api/price-lists/suppliers/${encodeURIComponent(supplierId)}/group-mappings`,
      { body: { mappings } },
    );
  }

  /** The ByggeBasen subgroups this supplier's catalogue uses, biggest first. */
  listByggeBasenSubgroups(supplierId: string): Promise<ApiResult<ByggeBasenSubgroupDto[]>> {
    return this.http.get<ByggeBasenSubgroupDto[]>(
      `/api/price-lists/suppliers/${encodeURIComponent(supplierId)}/byggebasen-subgroups`,
    );
  }

  /**
   * Replaces the company's ByggeBasen subgroup mappings. Not supplier-scoped: the
   * mapping applies to every ByggeBasen price book the company imports.
   */
  saveByggeBasenMappings(
    mappings: CompanyByggeBasenMappingInput[],
  ): Promise<ApiResult<SaveByggeBasenMappingsResultDto>> {
    return this.http.put<SaveByggeBasenMappingsResultDto>("/api/price-lists/byggebasen-mappings", {
      body: { mappings },
    });
  }

  recategorize(supplierId: string): Promise<ApiResult<number>> {
    return this.http.post<number>(
      `/api/price-lists/suppliers/${encodeURIComponent(supplierId)}/recategorize`,
      {},
    );
  }
}
